import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { ROUTES } from '#/config/routes';
import useDetailTable from '#/hooks/table/useDetailTable';
import { Button } from '#/modules/commonComponents/button';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import CustomerOrderSalesOrderPreviewDialog
  from '#/modules/crmMgmt/customerOrder/components/CustomerOrderSalesOrderPreviewDialog';
import { displayOrDash } from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderMeta';
import { mapCustomerOrderSalesOrderRows } from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderSalesOrders';
import SalesOrderStatusBadge from '#/modules/crmMgmt/salesOrder/components/SalesOrderStatusBadge';
import { formatDateOrDash } from '#/utils/date';

function PreviewProductsCell({ salesOrder }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button type="button" size="sm" onClick={() => setOpen(true)}>
        預覽
      </Button>
      <CustomerOrderSalesOrderPreviewDialog
        open={open}
        onOpenChange={setOpen}
        salesOrder={salesOrder}
      />
    </>
  );
}

PreviewProductsCell.propTypes = {
  salesOrder: PropTypes.object.isRequired,
};

function buildSalesOrderColumns() {
  return [
    {
      id: 'previewLines',
      header: '預覽',
      accessorFn: (row) => (row.previewLines ?? []).length,
      size: 88,
      enableSorting: false,
      cell: ({ row }) => <PreviewProductsCell salesOrder={row.original} />,
    },
    {
      id: 'salesOrderNo',
      header: '銷貨單號',
      accessorFn: (row) => displayOrDash(row.salesOrderNo),
      size: 150,
      enableSorting: false,
      cell: ({ row }) => {
        const id = row.original.id;
        const salesOrderNo = displayOrDash(row.original.salesOrderNo);
        return (
          <TruncatedTextWithTooltip
            text={salesOrderNo}
            detailPath={id ? ROUTES.salesOrderDetail(id) : null}
            tooltipContent={id ? '雙擊開啟單據（新分頁）' : null}
          />
        );
      },
      meta: { cellClassName: 'td_left' },
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 110,
      enableSorting: false,
      cell: ({ row }) => (
        row.original.status
          ? <SalesOrderStatusBadge status={row.original.status} size="sm" />
          : displayOrDash(null)
      ),
      meta: {
        /** 避免 DetailTable 把 status 當 entity icon；SO SHIPPING ≠ CO SHIPPED */
        statusPresentation: 'badge',
      },
    },
    {
      id: 'plannedShipDate',
      header: '預交日',
      accessorFn: (row) => formatDateOrDash(row.plannedShipDate),
      size: 120,
      enableSorting: false,
    },
  ];
}
const CustomerOrderSalesOrdersSection = ({ salesOrders = [] }) => {
  const columns = useMemo(() => buildSalesOrderColumns(), []);
  const rows = useMemo(
    () => mapCustomerOrderSalesOrderRows(salesOrders),
    [salesOrders],
  );

  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row, index) => String(row.id ?? `so-${index}`),
  });

  if (rows.length === 0) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">尚無銷貨單</p>;
  }

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
      />
    </div>
  );
};

CustomerOrderSalesOrdersSection.propTypes = {
  salesOrders: PropTypes.arrayOf(PropTypes.object),
};

export default CustomerOrderSalesOrdersSection;
